import { inject } from 'mobx-react';
import * as React from 'react';

import { AnalyticsStore } from 'bernie-core';
import { LocalizedComponentProps, withLocalization } from 'bernie-l10n';

import { GalleryViewModel, HotelViewModel, PinnedHotelViewModel, PropertyListingBadgeModel, ShortlistModel } from 'models';

import { UitkFigureAspectRatioType } from 'uitk-react-images';

import { ListingGalleryProps } from 'components/search/listing/internals/listing-gallery';
import { TitleBadge } from './title-badge';
import { TitleGallery } from './title-gallery';
import { TitleHeading } from './title-heading';
import { TitleThumbnail } from './title-thumbnail';

export interface TitleProps {
  analytics: AnalyticsStore;
  propertyListingBadgeModel: PropertyListingBadgeModel;
  className: string;
  currentHotel;
  imageUrl: string;
  gallery?: GalleryViewModel;
  fallbackImageUrl: string;
  lazyLoadImg: boolean;
  name: string;
  pinned?: PinnedHotelViewModel;
  stars: HotelViewModel['stars'];
  starRatingIcon: string;
  travelAd: {
    impressionTrackingUrl: string;
  };
  shortlistModel: ShortlistModel;
  thumbnail: object;
  infositeUrl: string;
  price: object;
  reviews: object;
  hotelId: number;
  referralObject: object;
  displayViewed: boolean;
  neighborhood: string;
  displaySecondaryBadge?: boolean;
  displayHeading?: boolean;
  /* exp27314 - pwa_mobile_PhotosHeight */
  ratio: UitkFigureAspectRatioType;
  listingClickHandler: () => void;
  onFavoriteClick?: () => void;
  mapListingView?: boolean;
}

export type LocalizedTitleProps = LocalizedComponentProps & TitleProps;

@withLocalization
@inject('analytics', 'currentHotel')
export class Title extends React.Component<LocalizedTitleProps> {
  constructor(props) {
    super(props);

    this.queuedImageFallbackcb = this.queuedImageFallbackcb.bind(this);
  }

  public static defaultProps: Partial<LocalizedTitleProps> = {
    displaySecondaryBadge: true,
    displayHeading: true
  };

  public render() {
    const {
      className,
      displayHeading,
      pinned,
      analytics,
      currentHotel,
      imageUrl,
      fallbackImageUrl,
      lazyLoadImg,
      propertyListingBadgeModel,
      ratio,
      travelAd,
      gallery,
      hotelId,
      price,
      reviews,
      shortlistModel,
      stars,
      infositeUrl,
      name,
      l10n,
      displaySecondaryBadge,
      displayViewed,
      referralObject,
      listingClickHandler,
      onFavoriteClick,
      mapListingView
    } = this.props;

    const favButtonText = {
      labelSave: l10n.formatText('hotels.searchResults.shortlist.saveToList.a11yLabel', this.props.name),
      labelRemove: l10n.formatText('hotels.searchResults.shortlist.removeFromList.a11yLabel', this.props.name),
      onSaveMessage: l10n.formatText('hotels.shortlist.saveToList.notification', this.props.name)
    };

    const starsStr = stars ? stars.toString() : null;
    const renderGal: ListingGalleryProps = {
      analytics,
      currentHotel,
      gallery,
      hotelId,
      hotelName: name,
      id: hotelId,
      price,
      reviews,
      shortlistModel,
      stars: starsStr,
      navigateOnClick: true,
      hideGalleryTrigger: true,
      hideImgNumber: true,
      imgClickDestinationUrl: infositeUrl,
      sendImgIndexInAnalytics: true,
      ratio,
      listingClickHandler,
      onFavoriteClick,
      mapListingView
    };

    const renderImg: RenderImageProps = {
      fallbackImageSrc: fallbackImageUrl,
      name,
      propertyListingBadgeModel,
      travelAd,
      ratio,
      displaySecondaryBadge,
      displayViewed,
      referralObject,
      shortlistModel,
      favButtonText,
      imageUrl,
      queuedImageFallbackcb: this.queuedImageFallbackcb
    };

    const renderlistingGalleryProps: RenderGalleryProps = {
      listingGalleryProps: renderGal,
      renderImageProps: renderImg,
      lazyLoadImg
    };

    const titleHeading = displayHeading && pinned && pinned.heading && <TitleHeading headingInfo={pinned} />;
    const cssClassName = `${className || ''} title`;

    if (!gallery || gallery.images.length === 0) {
      return (
        <header className={cssClassName}>
          {titleHeading}
          <TitleThumbnail imageUrl={imageUrl} lazyLoadImg={lazyLoadImg} renderImg={renderImg} />
        </header>
      );
    }

    return (
      <header className={cssClassName}>
        {titleHeading}
        <div className="listing-photo-gallery title__media-container">
          <TitleBadge
            propertyListingBadgeModel={propertyListingBadgeModel}
            travelAd={travelAd}
            displaySecondaryBadge={displaySecondaryBadge}
            hideSponsored={mapListingView}
          />
          {TitleGallery({ ...renderlistingGalleryProps })}
        </div>
      </header>
    );
  }

  private queuedImageFallbackcb(img, queuedImage): void {
    if (img && img.length > 0) {
      queuedImage.setState({ src: img });
    }
  }
}

export interface RenderImageProps {
  propertyListingBadgeModel: PropertyListingBadgeModel;
  fallbackImageSrc: string;
  name: string;
  travelAd: {
    impressionTrackingUrl: string;
  };
  ratio: UitkFigureAspectRatioType;
  displayViewed: boolean;
  referralObject: object;
  shortlistModel: ShortlistModel;
  favButtonText: {
    labelSave: string;
    labelRemove: string;
    onSaveMessage: string;
  };
  imageUrl: string;
  displaySecondaryBadge: boolean;
  queuedImageFallbackcb: (img: any, queuedImage: any) => void;
}

export interface RenderGalleryProps {
  listingGalleryProps: ListingGalleryProps;
  renderImageProps: RenderImageProps;
  lazyLoadImg: boolean;
  displayTransparentNav?: boolean;
}
